import { Prompt } from '@/types';
import PromptCard from './PromptCard';
import PromptListRow from './PromptListRow';

export type GridLayout = 'masonry' | 'uniform' | 'list';

interface Props {
  prompts: Prompt[];
  loading?: boolean;
  layout?: GridLayout;
  onSaveToggle?: (id: string, saved: boolean) => void;
  showActions?: boolean;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  emptyMessage?: string;
}

function SkeletonCard() {
  return (
    <div style={{
      background: 'var(--bg-raised)',
      border: '1px solid var(--line)',
      borderRadius: 'var(--radius-md)',
      overflow: 'hidden',
    }} className="animate-pulse">
      <div className="aspect-[4/5]" style={{ background: 'var(--bg-soft)' }} />
      <div className="p-4 space-y-3">
        <div className="h-4 rounded w-3/4" style={{ background: 'var(--bg-soft)' }} />
        <div className="h-3 rounded w-full" style={{ background: 'var(--bg-soft)' }} />
      </div>
    </div>
  );
}

export default function PromptGrid({
  prompts,
  loading,
  layout = 'masonry',
  onSaveToggle,
  showActions,
  onEdit,
  onDelete,
  emptyMessage = 'No prompts found.',
}: Props) {
  if (loading) {
    if (layout === 'list') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 900 }} className="animate-pulse">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{
              height: 76, borderRadius: 'var(--radius-md)',
              background: 'var(--bg-raised)', border: '1px solid var(--line)',
            }} />
          ))}
        </div>
      );
    }
    if (layout === 'uniform') {
      return (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 20,
        }}>
          {Array.from({ length: 9 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      );
    }
    return (
      <div className="masonry-grid">
        {Array.from({ length: 9 }).map((_, i) => (
          <div key={i} className="masonry-item">
            <SkeletonCard />
          </div>
        ))}
      </div>
    );
  }

  if (prompts.length === 0) {
    return (
      <div className="ps-empty">
        <h3>{emptyMessage}</h3>
      </div>
    );
  }

  if (layout === 'list') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 900 }}>
        {prompts.map((p) => (
          <PromptListRow key={p.id} prompt={p} />
        ))}
      </div>
    );
  }

  if (layout === 'uniform') {
    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: 20,
      }}>
        {prompts.map((prompt) => (
          <PromptCard
            key={prompt.id}
            prompt={prompt}
            onSaveToggle={onSaveToggle}
            showActions={showActions}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="masonry-grid">
      {prompts.map((prompt) => (
        <div key={prompt.id} className="masonry-item">
          <PromptCard
            prompt={prompt}
            onSaveToggle={onSaveToggle}
            showActions={showActions}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        </div>
      ))}
    </div>
  );
}
